import { type ReactNode, useCallback, useMemo, useRef, useState } from 'react';

import { Flex } from '@/components/base';
import treeSelectUtils from '@/components/Select/treeSelectUtils';
import type { TreeSelectItemBase } from '@/components/Select/types';
import { MainText } from '@/components/Typography/NamedTypo';
import { CHART_SOURCE_SEPERATOR } from '@/entities/chart/model/constants';
import TagOptionNode from '@/features/Feedback/field/Tag/TagOptionNode';
import useTopicLevelIcon from '@/features/Taxonomy/hooks/useTopicLevelIcon';
import taxonomyUtils from '@/features/Taxonomy/utils/taxonomy';
import service from '@/services/index';
import useFeedbackSourceStore from '@/stores/Feedback/FeedbackSourceStore';
import useTagStore from '@/stores/TagStore';
import useTaxonomyStore from '@/stores/TaxonomyStore';
import { getFeedbackSourceName, getSourceIcon } from '@/types/feedbackConst';
import type { TOPIC_2_TYPE } from '@/types/taxonomyConst';
import { getTagPath } from '@/utils/tag';

type TopicTreeWithTaxonomy = TopicTreeT & {
  taxonomy: {
    title: string;
    alias: string;
  };
};

const useFetchTaxonomyTree = () => {
  const taxonomyMap = useTaxonomyStore((state) => state.topicTreeMap);
  const [topicTreeMap, setTopicTreeMap] = useState<
    Record<string, TopicTreeWithTaxonomy>
  >({});
  const getTopicLevelIcon = useTopicLevelIcon();

  const initialized = useRef(false);

  const fetchData = useCallback(
    async (searchValue?: string): Promise<TreeSelectItemBase[]> => {
      if (initialized.current) {
        if (searchValue) {
          let result: TreeSelectItemBase[] = [];
          for (const topicTree of Object.values(topicTreeMap)) {
            let newRoot: string[] = [];
            for (const topic of Object.values(topicTree.topicMap)) {
              if (
                topic.name
                  .toLocaleLowerCase()
                  .includes(searchValue?.toLocaleLowerCase())
              ) {
                newRoot.push(topic.alias);
              }
              continue;
            }

            const topicMap = Object.values(topicTree.topicMap).reduce(
              (acc, topic) => {
                acc[topic.alias] = {
                  ...topic,
                  taxonomy_name: topicTree.taxonomy.title,
                } as TopicWithTaxonomyName;
                return acc;
              },
              {} as Record<string, TopicWithTaxonomyName>,
            );

            result.push(...mapChildTopic(topicMap, newRoot, getTopicLevelIcon));
          }

          return result;
        }
        const tree = buildTaxonomyTopicTree(topicTreeMap, getTopicLevelIcon);
        return tree;
      } else {
        const topicTrees = Object.values(taxonomyMap);
        const res = topicTrees.map((topicTree) => {
          const topics = Object.values(topicTree.topicMap);
          const newTopicMap: Record<
            string,
            Topic2TreeItemT<TOPIC_2_TYPE>
          > = topicTree.topicMap;
          return {
            taxonomy: topicTree.taxonomy,
            topics: topics,
            newTopicMap,
          };
        });

        const newTopicTreeMap: Record<string, TopicTreeWithTaxonomy> = {};
        for (const taxonomy of res) {
          newTopicTreeMap[taxonomy.taxonomy.alias] = {
            root: taxonomy.topics
              .filter((t) => t.level === 1)
              .map((t) => t.alias),
            topicMap: taxonomy.newTopicMap,
            taxonomy: taxonomy.taxonomy,
          };
        }
        setTopicTreeMap(newTopicTreeMap);
        initialized.current = true;

        return buildTaxonomyTopicTree(newTopicTreeMap, getTopicLevelIcon);
      }
    },
    [getTopicLevelIcon, taxonomyMap, topicTreeMap],
  );

  return fetchData;
};

const mapChildTopic = (
  topicMap: Record<string, TopicWithTaxonomyName>,
  topicAliases: string[],
  getTopicLevelIcon: (level: number) => ReactNode,
): TreeSelectItemBase[] => {
  return topicAliases.map((alias) => {
    const node = topicMap[alias];
    const paths = taxonomyUtils.getTopicPath(node).map((t) => t.label);
    return {
      alias: node.alias,
      searchValue: paths.join(' / '),
      childNodes: mapChildTopic(
        topicMap,
        node.children || [],
        getTopicLevelIcon,
      ),
      node: (
        <Flex g={8} a="center">
          <Flex a="center" className="ticket-area">
            {getTopicLevelIcon(node.level)}
          </Flex>
          <MainText>{node.name}</MainText>
        </Flex>
      ),
      meta: {
        level: node.level || 0,
      },
    };
  });
};

const buildTaxonomyTopicTree = (
  topicTreeMap: Record<string, TopicTreeWithTaxonomy>,
  getTopicLevelIcon: (level: number) => ReactNode,
) => {
  const result: TreeSelectItemBase[] = [];

  for (const topics of Object.values(topicTreeMap)) {
    const topicTree = topics;
    const { topicMap, root, taxonomy } = topicTree;

    const topicTreeMap = Object.values(topicMap).reduce(
      (acc, topic) => {
        acc[topic.alias] = {
          ...topic,
          taxonomy_name: taxonomy.title,
        } as TopicWithTaxonomyName;
        return acc;
      },
      {} as Record<string, TopicWithTaxonomyName>,
    );

    const topicNodes = root.map((topic) => {
      const topicItem = topicMap[topic];
      return {
        alias: topicItem.alias,
        searchValue: topicItem.name,
        node: (
          <Flex g={8} a="center">
            <Flex a="center" className="ticket-area">
              {getTopicLevelIcon(topicItem.level)}
            </Flex>
            <MainText>{topicItem.name}</MainText>
          </Flex>
        ),
        childNodes: mapChildTopic(
          topicTreeMap,
          topicItem.children || [],
          getTopicLevelIcon,
        ),
        meta: {
          level: topicItem.level || 0,
        },
      };
    });

    result.push({
      alias: taxonomy.alias,
      searchValue: taxonomy.title,
      checkOption: {
        disable: true,
      },
      node: <MainText># {taxonomy.title}</MainText>,
      childNodes: topicNodes,
      meta: {
        level: 0,
      },
    });
  }

  return result;
};

const useFetchTagTree = () => {
  const tagMap = useTagStore((state) => state.tagMap);
  const mapChildTag = useCallback(
    (tagAliases: string[]): TreeSelectItemBase[] => {
      return tagAliases.map((alias) => {
        const node = tagMap[alias];
        const paths = getTagPath(tagMap, alias).map((tag) => tag.name);
        const parents = paths.slice(0, -1);
        return {
          alias: node.alias,
          searchValue: paths.join(' / '),
          childNodes: mapChildTag(node.children),
          node: <TagOptionNode tag={node} parentNames={parents} />,
          meta: {
            level: node.level || 0,
          },
        };
      });
    },
    [tagMap],
  );

  const lv1TagNodes = useMemo(() => {
    return Object.values(tagMap)
      .filter((tag) => tag.level === 1)
      .map((tag) => tag.alias);
  }, [tagMap]);

  const tagTreeFetcher = useCallback(
    async (searchValue?: string) => {
      const data = await mapChildTag(lv1TagNodes);
      if (searchValue) {
        const filtered = treeSelectUtils.getFlattenChildNodes(
          data,
          searchValue,
        );
        return filtered;
      }
      return data;
    },
    [lv1TagNodes, mapChildTag],
  );

  return tagTreeFetcher;
};

const useFetchRemarkTree = () => {
  const fetchData = useCallback(async (searchValue?: string) => {
    if (!searchValue) return [];
    const res = await service.feedback.searchRemarks(searchValue);
    return res.remarks.map((remark) => {
      return {
        alias: remark,
        searchValue: remark,
        childNodes: [],
        node: remark,
      };
    });
  }, []);

  return fetchData;
};

const useFetchSourceTree = () => {
  const sources = useFeedbackSourceStore((state) => state.sourceInfo);
  const fetchData = useCallback(
    async (searchValue?: string): Promise<TreeSelectItemBase[]> => {
      return (
        sources?.map((source) => {
          const childnodes =
            source.domains?.map((domain) => {
              return {
                alias: `${source.source}${CHART_SOURCE_SEPERATOR}${domain.id}`,
                searchValue: domain.name,
                node: domain.name,
                childNodes: domain.channels?.map((channel) => {
                  return {
                    alias: `${source.source}${CHART_SOURCE_SEPERATOR}${domain.id}${CHART_SOURCE_SEPERATOR}${channel.id}`,
                    searchValue: channel.name,
                    node: channel.name,
                  };
                }),
              };
            }) || [];

          const sourceName = getFeedbackSourceName(source.source);
          return {
            alias: source.source.toString(),
            searchValue: sourceName,
            node: (
              <Flex a="center" g={8}>
                {getSourceIcon(source.source)}
                {sourceName}
              </Flex>
            ),
            childNodes: childnodes,
          };
        }) || []
      );
    },
    [getSourceIcon, sources],
  );

  return fetchData;
};

const feedbackPropertyFetcher = {
  useFetchRemarkTree,
  useFetchTagTree,
  useFetchSourceTree,
  useFetchTaxonomyTree,
};

export default feedbackPropertyFetcher;
